import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client';
import { genreOptions } from '../Results/discoveryOptions';
import './TasteDashboard.css';
import '../Taste/Taste.css';

const genreName = new Map(genreOptions.map(option => [option.value, option.label]));
const headshot = (path: string) => `https://image.tmdb.org/t/p/w185${path}`;
const monthLabel = (month: string, style: 'short' | 'long' = 'short') => new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: style, year: style === 'long' ? 'numeric' : undefined, timeZone: 'UTC' });
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

// Vertical columns: one series, value on the cap, tooltip on hover/focus.
const Columns = ({ data, label }: { data: { key: string; axis: string; value: number; tip: string }[]; label: string }) => {
  const max = Math.max(1, ...data.map(item => item.value));
  return <div className="columns" role="list" aria-label={label}>
    {data.map(item => <div key={item.key} className="columns__slot" role="listitem" tabIndex={0} aria-label={item.tip} data-tip={item.tip}>
      <span className="columns__value">{item.value || ''}</span>
      <span className="columns__bar" style={{ height: `${(item.value / max) * 100}%` }} />
      <span className="columns__axis">{item.axis}</span>
    </div>)}
  </div>;
};

const TasteDashboard = () => {
  const [taste, setTaste] = useState<any>(null);
  useEffect(() => { apiRequest<any>('/taste').then(setTaste).catch(() => undefined); }, []);
  if (!taste) return null;
  const { totals } = taste;
  if (!totals.answered) return <section className="taste taste--empty"><h2>Let’s learn your taste</h2><p>Swipe through a few films and your taste profile will appear here.</p><Link to="/swipe">Start swiping →</Link></section>;

  const genres = taste.topGenres.slice(0, 6);
  const genreMax = Math.max(1, ...genres.map((genre: any) => genre.count));
  const favourites: any[] = taste.favourites || [];
  const derived: any[] = (taste.topActors || []).filter((actor: any) => !actor.favourite);

  return <section className="taste" aria-labelledby="taste-heading">
    <h2 id="taste-heading" className="visually-hidden">Your taste in numbers</h2>
    <div className="taste__tiles">
      <div className="tile"><b>{totals.seen}</b><span>films seen</span></div>
      <div className="tile"><b>{taste.averageRating ? `${taste.averageRating}★` : '–'}</b><span>average from {plural(totals.rated, 'rating')}</span></div>
      <div className="tile"><b>{totals.liked}<em> / {totals.disliked}</em></b><span>liked / didn’t like</span></div>
      <div className="tile"><b>{totals.watchlist}</b><span>on your watchlist</span></div>
      <div className="tile"><b>{totals.together}</b><span>picked with friends</span></div>
    </div>

    <div className="taste__grid">
      <article className="taste-card">
        <h3>How you rate</h3><p>{totals.rated ? `${plural(totals.rated, 'film')} rated` : 'Rate films to see your spread'}</p>
        <Columns label="Ratings by star" data={taste.ratingDistribution.map((bucket: any) => ({ key: String(bucket.stars), axis: `${bucket.stars}★`, value: bucket.count, tip: `${bucket.stars}★: ${plural(bucket.count, 'film')}` }))} />
      </article>

      <article className="taste-card">
        <h3>Your genres</h3><p>What you’ve seen most</p>
        {genres.length ? <ol className="hbars">{genres.map((genre: any) => <li key={genre.genreId} tabIndex={0} data-tip={`${genreName.get(genre.genreId) || 'Other'}: ${plural(genre.count, 'film')}${genre.averageRating ? `, average ${genre.averageRating}★` : ''}${genre.liked ? `, ${genre.liked} liked` : ''}`}>
          <span className="hbars__label">{genreName.get(genre.genreId) || 'Other'}</span>
          <span className="hbars__track"><span className="hbars__bar" style={{ width: `${(genre.count / genreMax) * 100}%` }} /><b>{genre.count}</b></span>
        </li>)}</ol> : <p className="taste-card__empty">Log a few films to see your genres.</p>}
      </article>

      <article className="taste-card taste-card--wide">
        <h3>Actors</h3><p>Loved on swipes and film pages, and the ones who keep turning up in films you liked</p>
        {taste.actors && taste.actors.like + taste.actors.dislike + taste.actors.unknown + taste.actors.neutral > 0 && <p className="actor-counts"><span><b>{taste.actors.like}</b> loved</span><span><b>{taste.actors.dislike}</b> not a fan</span><span><b>{taste.actors.neutral}</b> don’t mind</span><span><b>{taste.actors.unknown}</b> never heard of</span><Link to="/swipe?type=actors">Swipe more actors →</Link></p>}
        {favourites.length || derived.length ? <div className="actors">
          {favourites.map(actor => <div key={`f-${actor.personId}`} className="actor">{actor.profilePath ? <img src={headshot(actor.profilePath)} alt="" /> : <span className="actor__blank" />}<strong>{actor.name}</strong><small><span aria-hidden="true">♥ </span>Favourite</small></div>)}
          {derived.map(actor => <div key={`d-${actor.id}`} className="actor" title={actor.filmTitles.join(', ')}>{actor.profilePath ? <img src={headshot(actor.profilePath)} alt="" /> : <span className="actor__blank" />}<strong>{actor.name}</strong><small>{plural(actor.films, 'film')}{actor.liked ? ` · ${actor.liked} liked` : ''}</small></div>)}
        </div> : <p className="taste-card__empty">Swipe through actors, heart them on film pages, or keep logging films. Actors who appear in two or more of your films show up here too.</p>}
        {taste.actors?.disliked?.length > 0 && <div className="actors__row"><h4>Not a fan</h4><div className="actors">{taste.actors.disliked.map((actor: any) => <div key={`x-${actor.personId}`} className="actor actor--dim">{actor.profilePath ? <img src={headshot(actor.profilePath)} alt="" /> : <span className="actor__blank" />}<strong>{actor.name}</strong></div>)}</div></div>}
      </article>

      <article className="taste-card taste-card--wide">
        <h3>Films logged</h3><p>Seen and rated over the last 12 months</p>
        <Columns label="Films logged per month" data={taste.activity.map((month: any) => ({ key: month.month, axis: monthLabel(month.month).slice(0, 1), value: month.seen, tip: `${monthLabel(month.month, 'long')}: ${plural(month.seen, 'film')} logged, ${month.rated} rated` }))} />
      </article>
    </div>
  </section>;
};

export default TasteDashboard;
